import type { settingTypes } from '@ipfabric/utils';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, type FormikProps, useFormik } from 'formik';
import { type FC, memo } from 'react';
import { validateSlug } from 'utils/formValidation/validations';
import { hasHttpOrHttps } from 'utils/hasHttpOrHtttps';
import { VendorDialogContainer } from './VendorDialogContainer';
import { FormikAdvancedSettings } from './fields/FormikAdvancedSettings';
import { FormikRateLimitingSettingsFields } from './fields/FormikRateLimitingSettingsFields';
import { FormikVendorApiBaseUrlField } from './fields/FormikVendorApiBaseUrlField';
import { FormikVendorApiCombinedDiscoveryField } from './fields/FormikVendorApiCombinedDiscoveryField';
import { FormikVendorApiCommentField } from './fields/FormikVendorApiCommentField';
import { FormikVendorApiEnabledField } from './fields/FormikVendorApiEnabledField';
import { FormikVendorApiRespectSystemProxyConfigurationField } from './fields/FormikVendorApiRejectUnauthorizedField';
import { FormikVendorApiRejectUnauthorizedField } from './fields/FormikVendorApiRespectSystemProxyConfigurationField';
import { FormikVendorApiSlugField } from './fields/FormikVendorApiSlugField';
import { FormikVendorApiUsernamePasswordFields } from './fields/FormikVendorApiUsernamePasswordFields';
import { FormikViptelaDataErrorHandlingSettingsFields } from './fields/FormikViptelaDataErrorHandlingSettingsFields';
import { getAdvancedSettingsDefaultValues } from './helpers/applyDefaultInitialValues';
import { isVendorUpdate } from './helpers/guardTypes';
import type { BaseVendorFormValues, VendorDialogProps } from './vendorTypes';

export type ViptelaFormValues =
  BaseVendorFormValues<settingTypes.ViptelaApiSettings>;

type Props = VendorDialogProps<
  ViptelaFormValues,
  settingTypes.ViptelaApiSettings,
  settingTypes.UpdateViptelaApiSettings
>;

const resetSecretAttributes = (
  values: ViptelaFormValues,
): ViptelaFormValues => {
  return {
    ...values,
    username: '',
    password: '',
  };
};

const BareViptelaForm: FC<Props> = (props) => {
  const editMode = props.dialogType === 'edit';
  const msg = useLocale();
  const { validations } = msg.discoverySettings.vendorsApi;
  const formik = useFormik<ViptelaFormValues>({
    initialValues: resetSecretAttributes(props.initialValues),
    validate: (values) => {
      const errors: FormikErrors<ViptelaFormValues> = {};

      if (!values.baseUrl) {
        errors.baseUrl = validations.required;
      } else if (!hasHttpOrHttps(values.baseUrl)) {
        errors.baseUrl = validations.baseUrlStartsWith;
      }
      if (!editMode && !values.username) {
        errors.username = validations.required;
      }
      if (!editMode && !values.password) {
        errors.password = validations.required;
      }
      validateSlug(values, errors, msg);

      return errors;
    },
    onSubmit: (values: ViptelaFormValues) => {
      const formattedValues = {
        ...values,
        ...getAdvancedSettingsDefaultValues(values),
      };

      if (isVendorUpdate(values) && props.dialogType === 'edit') {
        props.onSubmit({
          ...formattedValues,
          id: values.id,
          username:
            values.username || props.initialValues.username || undefined,
          password:
            values.password || props.initialValues.password || undefined,
        });
      } else if (props.dialogType === 'add') {
        props.onSubmit(formattedValues);
      }
    },
  });

  return (
    <VendorDialogContainer
      formik={formik as FormikProps<ViptelaFormValues>}
      dialogType={props.dialogType}
      {...props.vendorDialogContainerPassthruProps}
    >
      <FormikVendorApiEnabledField formik={formik} />
      <FormikVendorApiUsernamePasswordFields
        formik={formik}
        isEditMode={editMode}
      />
      <FormikVendorApiBaseUrlField
        formik={formik}
        placeholder='https://vManage:8443'
      />
      <FormikVendorApiSlugField formik={formik} />
      <FormikVendorApiCommentField formik={formik} />
      <FormikVendorApiRespectSystemProxyConfigurationField formik={formik} />
      <FormikVendorApiRejectUnauthorizedField formik={formik} />
      <FormikVendorApiCombinedDiscoveryField formik={formik} />
      <FormikAdvancedSettings>
        <FormikRateLimitingSettingsFields formik={formik} />
        <FormikViptelaDataErrorHandlingSettingsFields formik={formik} />
      </FormikAdvancedSettings>
    </VendorDialogContainer>
  );
};

export const ViptelaForm = memo(BareViptelaForm);
